import { notification } from 'antd';

export const onDownload = (url: string): void => {
  const suffix = url.slice(url.lastIndexOf('.'));
  const filename = Date.now() + suffix;
  console.log("Downloading image...", url, filename);
  fetch(url, { mode: 'cors', cache: 'no-cache' })
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      // Delay revocation to allow the download to start properly
      setTimeout(() => {
        URL.revokeObjectURL(blobUrl);
        link.remove();
      }, 100);
    })
    .catch((error) => {
      console.error("Download failed:", error);
    });
};

export const reuseImage = (
  imageUrl: string,
  setImagePreviewUrl: (url: string | null) => void,
  setSelectedImage: (file: File | null) => void,
  inputRef: React.RefObject<HTMLDivElement>
): void => {
  // For S3 bucket images that may have CORS issues
  if (imageUrl.includes('s3') && imageUrl.includes('amazonaws.com')) {
    // Use image element approach which works better with CORS for images
    const img = new window.Image(); // Use window.Image to avoid TypeScript errors
    img.crossOrigin = "anonymous";
    
    // Set up a direct fetch with proper headers to handle CORS
    const fetchHeaders = new Headers();
    fetchHeaders.append('Origin', window.location.origin);
    
    // Try to fetch image headers first to ensure CORS headers are correct
    fetch(imageUrl, {
      method: 'HEAD',
      headers: fetchHeaders,
      mode: 'cors',
      cache: 'no-store'
    })
    .then(() => {
      // If we get here, CORS might be properly configured
      img.onload = () => {
        try {
          // Create canvas to get image data
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          
          // Draw image to canvas
          ctx?.drawImage(img, 0, 0);
          
          // Get data URL directly for preview
          const dataUrl = canvas.toDataURL('image/jpeg');
          setImagePreviewUrl(dataUrl);
          
          // Convert to blob for upload
          canvas.toBlob((blob) => {
            if (blob) {
              const file = new File([blob], `reused-image-${Date.now()}.jpg`, { 
                type: 'image/jpeg',
                lastModified: new Date().getTime()
              });
              setSelectedImage(file);
              
              // Focus on input after adding image
              if (inputRef.current) {
                inputRef.current.focus();
              }
            }
          }, 'image/jpeg', 0.95);
        } catch (error) {
          console.error('Error processing image:', error);
          notification.error({
            message: 'Failed to Process Image',
            description: 'Could not process this image due to security restrictions.',
          });
        }
      };
      
      // Start loading the image with cache-busting parameter
      img.src = `${imageUrl}${imageUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
    })
    .catch(() => {
      // If HEAD request fails, try the fallback approach
      console.error('CORS issue detected, trying fallback method...');
    });
  } 
  else {
    // For regular images that don't have CORS restrictions
    const proxyUrl = imageUrl.startsWith('http') ? imageUrl : `https://cors-anywhere.herokuapp.com/${imageUrl}`;
    
    fetch(proxyUrl, {
      headers: {
        'Origin': window.location.origin
      },
      cache: 'no-cache'
    })
    .then(response => {
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      return response.blob();
    })
    .then(blob => {
      // Create a File object from the blob
      const file = new File([blob], `reused-image-${Date.now()}.jpg`, { type: 'image/jpeg' });
      setSelectedImage(file);
      
      // Create a preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        if (e.target?.result) {
          setImagePreviewUrl(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
      
      // Focus on the input after adding the image
      if (inputRef.current) {
        inputRef.current.focus();
      }
    })
    .catch(error => {
      console.error('Error reusing image:', error);
      notification.error({
        message: 'Failed to Reuse Image',
        description: 'Could not access this image. Try downloading it first.',
      });
    });
  }
};
